import { FileCheck2 } from "lucide-react";
import { Temporal } from "temporal-polyfill";
import { CredentialVerificationResult } from "@api/shared-runtime/enums";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { CredentialVerification } from "../../types";
import { VerificationSourceLink } from "../verification-source-link";

const OUTCOME_LABELS: Record<CredentialVerificationResult, string> = {
  [CredentialVerificationResult.Clear]: "Clear",
  [CredentialVerificationResult.Concern]: "Needs review",
  [CredentialVerificationResult.MissingInfo]: "Missing information",
  [CredentialVerificationResult.Error]: "Unable to verify",
};

/** Verification evidence belongs to the saved version, independently of its
 * supporting documents. It cannot be removed or moved to a different license. */
export function CredentialVerifications({
  verifications,
  onOpenEvidence,
}: {
  verifications: CredentialVerification[];
  onOpenEvidence?: (verification: CredentialVerification) => void;
}) {
  // The reader orders events by verifiedAt and id descending. Keep the newest
  // event even when an older one has evidence or a more favorable result.
  const [verification] = verifications;
  if (!verification) {
    return null;
  }
  const evidenceLabel = verification.registryReport
    ? "View verification details"
    : "View verification evidence";

  return (
    <section aria-label="Latest verification" className="space-y-3">
      <div className="space-y-1">
        <h3 className="text-sm font-semibold text-slate-900">
          Latest verification
        </h3>
        <p className="text-xs leading-relaxed text-slate-500">
          Most recent result for the saved version of this credential.
        </p>
      </div>
      <div className="space-y-2 rounded-xl border border-slate-200 bg-white p-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <p className="text-sm font-medium text-slate-900">
            {verification.source}
          </p>
          <span
            className={cn(
              "rounded-full px-2 py-0.5 text-xs font-medium",
              verification.result === CredentialVerificationResult.Clear
                ? "bg-emerald-50 text-emerald-700"
                : "bg-amber-50 text-amber-800",
            )}
          >
            {OUTCOME_LABELS[verification.result]}
          </span>
        </div>
        <time
          dateTime={verification.verifiedAt}
          className="block text-xs text-slate-500"
        >
          {Temporal.Instant.from(verification.verifiedAt).toLocaleString(
            undefined,
            {
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
            },
          )}
        </time>
        {verification.note && (
          <p
            className={cn(
              "whitespace-pre-wrap text-xs leading-relaxed",
              verification.result === CredentialVerificationResult.Clear
                ? "text-slate-600"
                : "text-amber-800",
            )}
          >
            {verification.note}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2">
          {(verification.evidence || verification.registryReport) &&
            onOpenEvidence && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-auto max-w-full gap-2 rounded-lg px-2.5 py-2 text-left text-xs text-primary"
                aria-label={`${evidenceLabel} from ${verification.source}`}
                onClick={() => onOpenEvidence(verification)}
              >
                <FileCheck2 className="size-4 shrink-0" aria-hidden="true" />
                {evidenceLabel}
              </Button>
            )}
          <VerificationSourceLink sourceUrl={verification.sourceUrl} />
        </div>
      </div>
    </section>
  );
}
